import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Check, X, ChevronUp, ArrowUpCircle, Eye, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TAB_KEYS = ["promoted", "candidates", "watching", "miscast"];
const TAB_LABELS = { promoted: "Auto-Promoted", candidates: "Candidates", watching: "Watching", miscast: "Miscast Queue" };
const TAB_COLORS = {
  promoted: "bg-emerald-500 text-white",
  candidates: "bg-amber-500 text-white",
  watching: "bg-sky-500 text-white",
  miscast: "bg-red-500 text-white",
};
const TAB_INACTIVE = "bg-slate-100 text-slate-600 hover:bg-slate-200";

export default function CategoryIntelligencePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("promoted");
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedTag, setExpandedTag] = useState(null);
  const [tagBusinesses, setTagBusinesses] = useState({});
  const [resolveForm, setResolveForm] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/category-intelligence/summary`);
      setData(res.data);
    } catch {
      toast.error("Failed to load category intelligence data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchBusinesses = async (tag) => {
    if (tagBusinesses[tag]) return;
    try {
      const res = await axios.get(`${API}/admin/category-intelligence/businesses/${tag}`);
      setTagBusinesses(prev => ({ ...prev, [tag]: res.data }));
    } catch {
      toast.error("Failed to load businesses");
    }
  };

  const handlePromote = async (tag) => {
    setActionLoading(tag);
    try {
      await axios.post(`${API}/admin/category-intelligence/promote/${tag}`);
      toast.success(`Promoted "${tag}" to Tier 1`);
      fetchData();
    } catch { toast.error("Promotion failed"); }
    finally { setActionLoading(null); }
  };

  const handleDismiss = async (tag) => {
    setActionLoading(tag);
    try {
      await axios.post(`${API}/admin/category-intelligence/dismiss/${tag}`);
      toast.success(`Dismissed "${tag}"`);
      fetchData();
    } catch { toast.error("Dismiss failed"); }
    finally { setActionLoading(null); }
  };

  const handleDemote = async (tag) => {
    setActionLoading(tag);
    try {
      await axios.post(`${API}/admin/category-intelligence/demote/${tag}`);
      toast.success(`Demoted "${tag}" back to pending`);
      fetchData();
    } catch { toast.error("Demotion failed"); }
    finally { setActionLoading(null); }
  };

  const handleResolveMiscast = async (businessId, action, suggested) => {
    setActionLoading(businessId);
    try {
      await axios.post(`${API}/admin/miscast-queue/resolve/${businessId}`, {
        action,
        suggested_categories: suggested || [],
      });
      toast.success(`Resolved miscast for ${businessId}`);
      fetchData();
    } catch { toast.error("Resolution failed"); }
    finally { setActionLoading(null); }
  };

  const handleBulkAccept = async () => {
    setActionLoading("bulk");
    try {
      const res = await axios.post(`${API}/admin/miscast-queue/bulk-accept`);
      toast.success(`Bulk accepted ${res.data.resolved} high-confidence entries`);
      fetchData();
    } catch { toast.error("Bulk accept failed"); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;
  if (!data) return <p className="text-xs text-slate-400 text-center py-4">No data available</p>;

  const counts = data.counts || {};

  const renderSubcategoryCard = (item, actions) => {
    const tag = item.subcategory_tag;
    const isExpanded = expandedTag === tag;
    return (
      <div key={tag} className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`ci-card-${tag}`}>
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">{item.display_name}</p>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-bold">{item.business_count || 0} biz</span>
              <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-bold">{item.source_count || 0} sources</span>
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold">{item.sweep_run_count || 0} sweeps</span>
              {item.intent_signal_count > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 text-[10px] font-bold">{item.intent_signal_count} intents</span>
              )}
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">score: {item.promotion_score || 0}/4</span>
              {item.dominant_age_restriction && (
                <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-bold">{item.dominant_age_restriction}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => { if (isExpanded) { setExpandedTag(null); } else { setExpandedTag(tag); fetchBusinesses(tag); } }} className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition" data-testid={`ci-expand-${tag}`}>
              <Eye className="w-3.5 h-3.5 text-slate-500" />
            </button>
            {actions}
          </div>
        </div>
        {isExpanded && tagBusinesses[tag] && (
          <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
            <p className="text-[10px] text-slate-400 font-bold uppercase mb-2">Linked Businesses ({tagBusinesses[tag].count})</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(tagBusinesses[tag].businesses || []).map(b => (
                <div key={b.business_id} className="flex items-center justify-between py-1 px-2 bg-white rounded-lg text-xs">
                  <span className="text-slate-700 truncate flex-1">{b.name}</span>
                  <span className="text-slate-400 ml-2">{b.city}</span>
                  <span className="text-slate-400 ml-2">{(b.categories || []).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="category-intelligence-panel">
      {/* Summary Counts */}
      <div className="grid grid-cols-4 gap-2">
        {TAB_KEYS.map(k => (
          <div key={k} className={`rounded-xl p-3 text-center border ${tab === k ? "border-slate-400 ring-1 ring-slate-300" : "border-slate-100"} bg-white`}>
            <p className="text-xl font-black text-slate-800">{counts[k === "miscast" ? "miscast" : k] || 0}</p>
            <p className="text-[10px] text-slate-500 font-semibold mt-0.5">{TAB_LABELS[k]}</p>
          </div>
        ))}
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1.5" data-testid="ci-tabs">
        {TAB_KEYS.map(k => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${tab === k ? TAB_COLORS[k] : TAB_INACTIVE}`} data-testid={`ci-tab-${k}`}>
            {TAB_LABELS[k]} ({counts[k === "miscast" ? "miscast" : k] || 0})
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "promoted" && (
        <div className="space-y-2" data-testid="ci-promoted-list">
          {(data.promoted || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No auto-promoted categories yet</p>
          ) : (data.promoted || []).map(item => renderSubcategoryCard(item, (
            <button onClick={() => handleDemote(item.subcategory_tag)} disabled={actionLoading === item.subcategory_tag} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-[10px] font-bold hover:bg-red-100 transition flex items-center gap-1" data-testid={`ci-demote-${item.subcategory_tag}`}>
              {actionLoading === item.subcategory_tag ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Demote
            </button>
          )))}
        </div>
      )}

      {tab === "candidates" && (
        <div className="space-y-2" data-testid="ci-candidates-list">
          {(data.candidates || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No candidate categories</p>
          ) : (data.candidates || []).map(item => renderSubcategoryCard(item, (
            <div className="flex gap-1">
              <button onClick={() => handlePromote(item.subcategory_tag)} disabled={actionLoading === item.subcategory_tag} className="px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-bold hover:bg-emerald-100 transition flex items-center gap-1" data-testid={`ci-promote-${item.subcategory_tag}`}>
                {actionLoading === item.subcategory_tag ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowUpCircle className="w-3 h-3" />} Promote
              </button>
              <button onClick={() => handleDismiss(item.subcategory_tag)} disabled={actionLoading === item.subcategory_tag} className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-bold hover:bg-slate-200 transition flex items-center gap-1" data-testid={`ci-dismiss-${item.subcategory_tag}`}>
                <X className="w-3 h-3" /> Dismiss
              </button>
            </div>
          )))}
        </div>
      )}

      {tab === "watching" && (
        <div className="space-y-2" data-testid="ci-watching-list">
          {(data.watching || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No provisional categories being watched</p>
          ) : (data.watching || []).map(item => renderSubcategoryCard(item, null))}
        </div>
      )}

      {tab === "miscast" && (
        <div className="space-y-3" data-testid="ci-miscast-list">
          {(data.miscast_queue || []).some(m => m.confidence > 0.8) && (
            <button onClick={handleBulkAccept} disabled={actionLoading === "bulk"} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition text-sm font-bold text-emerald-700 disabled:opacity-50" data-testid="ci-bulk-accept">
              {actionLoading === "bulk" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Bulk Accept High-Confidence ({(data.miscast_queue || []).filter(m => m.confidence > 0.8).length})
            </button>
          )}
          {(data.miscast_queue || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">Miscast queue is empty</p>
          ) : (data.miscast_queue || []).map(m => (
            <div key={m.business_id} className="border border-slate-200 rounded-xl bg-white p-4 space-y-2" data-testid={`ci-miscast-${m.business_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-800">{m.business_name || m.business_id}</p>
                  <p className="text-[10px] text-slate-400">Reason: {m.reason} | Confidence: {((m.confidence || 0) * 100).toFixed(0)}%</p>
                </div>
                <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.confidence > 0.7 ? "bg-emerald-50 text-emerald-700" : m.confidence > 0.4 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                  {m.confidence > 0.7 ? "High" : m.confidence > 0.4 ? "Medium" : "Low"}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-slate-50 rounded-lg">
                  <p className="text-[10px] text-slate-400 font-bold">Current</p>
                  <p className="text-xs text-slate-700">{(m.current_categories || []).join(", ") || "None"}</p>
                </div>
                {(m.suggested_categories || []).length > 0 && (
                  <div className="flex-1 px-3 py-2 bg-emerald-50 rounded-lg">
                    <p className="text-[10px] text-emerald-500 font-bold">Suggested</p>
                    <p className="text-xs text-emerald-700">{m.suggested_categories.join(", ")}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                {(m.suggested_categories || []).length > 0 && (
                  <button onClick={() => handleResolveMiscast(m.business_id, "accept_suggestion", m.suggested_categories)} disabled={actionLoading === m.business_id} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition" data-testid={`ci-accept-${m.business_id}`}>
                    {actionLoading === m.business_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accept
                  </button>
                )}
                <button onClick={() => handleResolveMiscast(m.business_id, "keep_current")} disabled={actionLoading === m.business_id} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 transition" data-testid={`ci-keep-${m.business_id}`}>
                  <X className="w-3.5 h-3.5" /> Keep Current
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
